import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function FunnelManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [funnels, setFunnels] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New funnel modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newFunnel, setNewFunnel] = useState({
    name: '',
    description: '',
    product_id: '',
    tripwire_id: ''
  });
  const [saving, setSaving] = useState(false);

  // Products for dropdowns
  const [products, setProducts] = useState([]);
  const [tripwires, setTripwires] = useState([]);
  const [defaultTripwireId, setDefaultTripwireId] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    fetchFunnels();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch('/api/funnels/products', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        setTripwires(data.tripwires || []);
        setDefaultTripwireId(data.default_tripwire_id);
        // Pre-set the default tripwire in the form
        if (data.default_tripwire_id) {
          setNewFunnel(prev => ({ ...prev, tripwire_id: data.default_tripwire_id }));
        }
      }
    } catch (err) {
      console.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchFunnels = async () => {
    try {
      const response = await fetch('/api/funnels', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFunnels(data.funnels || []);
      }
    } catch (err) {
      setError('Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  const createFunnel = async () => {
    if (!newFunnel.name.trim()) {
      setError('Please enter a funnel name');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch('/api/funnels', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFunnel)
      });

      if (response.ok) {
        const data = await response.json();
        setShowNewModal(false);
        setNewFunnel({ name: '', description: '', product_id: '', tripwire_id: '' });
        // Navigate to the funnel builder
        navigate(`/command-center/campaign-hq/funnel/${data.funnel.id}`);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create funnel');
      }
    } catch (err) {
      setError('Error creating funnel');
    } finally {
      setSaving(false);
    }
  };

  const deleteFunnel = async (funnelId) => {
    if (!window.confirm('Are you sure you want to delete this funnel? All components will be deleted.')) {
      return;
    }

    try {
      const response = await fetch(`/api/funnels/${funnelId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Funnel deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchFunnels();
      }
    } catch (err) {
      setError('Error deleting funnel');
    }
  };

  const toggleFunnel = async (funnelId) => {
    try {
      const response = await fetch(`/api/funnels/${funnelId}/toggle`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        fetchFunnels();
      }
    } catch (err) {
      setError('Error toggling funnel');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'paused':
        return <Badge bg="warning" text="dark">Paused</Badge>;
      case 'archived':
        return <Badge bg="secondary">Archived</Badge>;
      default:
        return <Badge bg="info">Draft</Badge>;
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading funnels...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Funnel Management</h1>
          <p className="text-muted mb-0">
            Create and manage your marketing funnels. Build lead magnets, landing pages, email sequences, and sales pages.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={() => setShowNewModal(true)}
        >
          + Create Funnel
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Funnels Grid */}
      {funnels.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
            <h4>No Funnels Yet</h4>
            <p className="text-muted mb-4">
              Create your first funnel to start building landing pages, email sequences, and sales pages.
            </p>
            <Button
              variant="dark"
              style={{ backgroundColor: colors.greyDarkest }}
              onClick={() => setShowNewModal(true)}
            >
              Create Your First Funnel
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {funnels.map(funnel => (
            <Col key={funnel.id} lg={4} md={6} className="mb-4">
              <Card className="shadow-sm h-100">
                <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.greyLightest }}>
                  <strong>{funnel.name}</strong>
                  {getStatusBadge(funnel.status)}
                </Card.Header>
                <Card.Body>
                  {funnel.description && (
                    <p className="text-muted small mb-3">{funnel.description}</p>
                  )}

                  <div className="mb-3">
                    <small className="text-muted d-block mb-2">Components:</small>
                    <div className="d-flex flex-wrap gap-1">
                      {funnel.component_counts && Object.entries(funnel.component_counts).map(([type, count]) => (
                        <Badge key={type} bg="light" text="dark" className="border">
                          {type.replace(/_/g, ' ')}: {count}
                        </Badge>
                      ))}
                      {funnel.component_count === 0 && (
                        <span className="text-muted small">No components yet</span>
                      )}
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    <Button
                      variant="dark"
                      style={{ backgroundColor: colors.greyDarkest }}
                      onClick={() => navigate(`/command-center/campaign-hq/funnel/${funnel.id}`)}
                    >
                      Edit Funnel
                    </Button>
                    <div className="d-flex gap-2">
                      <Button
                        variant={funnel.status === 'active' ? 'warning' : 'success'}
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => toggleFunnel(funnel.id)}
                        title={funnel.status === 'active' ? 'Pause this funnel' : 'Publish funnel pages to WordPress'}
                      >
                        {funnel.status === 'active' ? 'Pause' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteFunnel(funnel.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card.Body>
                <Card.Footer className="text-muted small">
                  Created: {new Date(funnel.created_at).toLocaleDateString()}
                  {funnel.updated_at !== funnel.created_at && (
                    <span className="ms-3">Updated: {new Date(funnel.updated_at).toLocaleDateString()}</span>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create Funnel Modal */}
      <Modal show={showNewModal} onHide={() => setShowNewModal(false)} onShow={() => {
        // Set default tripwire when modal opens
        if (defaultTripwireId && !newFunnel.tripwire_id) {
          setNewFunnel(prev => ({ ...prev, tripwire_id: defaultTripwireId }));
        }
      }}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Create New Funnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label><strong>Funnel Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Lead Magnet Funnel, Product Launch Funnel"
                value={newFunnel.name}
                onChange={(e) => setNewFunnel({ ...newFunnel, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Product *</strong></Form.Label>
              <Form.Select
                value={newFunnel.product_id}
                onChange={(e) => setNewFunnel({ ...newFunnel, product_id: e.target.value })}
                disabled={loadingProducts}
              >
                <option value="">Select the product this funnel sells...</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.price && `($${p.price})`} {p.position && `- ${p.position}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Which product will people buy at the end of this funnel?
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Tripwire (Optional)</strong></Form.Label>
              <Form.Select
                value={newFunnel.tripwire_id}
                onChange={(e) => setNewFunnel({ ...newFunnel, tripwire_id: e.target.value })}
                disabled={loadingProducts}
              >
                <option value="none">No tripwire - go straight to main offer</option>
                {tripwires.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.price && `($${p.price})`} {p.is_primary_tripwire && '⭐ Primary'}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Low-priced offer to convert leads before the main product.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Description</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What's this funnel for? e.g., 'Webinar funnel for consultants'"
                value={newFunnel.description}
                onChange={(e) => setNewFunnel({ ...newFunnel, description: e.target.value })}
              />
            </Form.Group>
          </Form>

          {products.length === 0 && !loadingProducts && (
            <Alert variant="warning">
              <strong>No products found!</strong> Create products in your Product Catalog first, then come back to set up funnels.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={createFunnel}
            disabled={saving || !newFunnel.name.trim() || !newFunnel.product_id}
          >
            {saving ? 'Creating...' : 'Create Funnel'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default FunnelManagement;
